/**
 * The completeness rule (DISCUSSION-keluarga, spec 13.3): the required columns of an
 * umat depend on their age band, and "Lengkap" on Beranda and in Laporan means every
 * required column is filled. Evaluated by the worker over rows, exposed as `kelengkapan`
 * per umat and per keluarga; feeds the list chip, the card line and the Beranda table.
 *
 * Bands (completed years):
 *   0 to 6    nama, jenis kelamin, tanggal lahir; baptis reference for a Catholic
 *   7 to 16   the same plus pendidikan
 *   17 and up the same plus NIK, pekerjaan and status perkawinan (KTP age)
 *
 * Without a birth date the age is unknown; the adult band applies so an incomplete record
 * is never reported complete because a date is missing.
 */
import { usiaPada } from './dates/partial'
import ui from './strings/ui.id.json' with { type: 'json' }
import { isiUnit, SEBUTAN_BAWAAN, type SebutanUnit } from './strings/unit'

export type KolomKelengkapanUmat =
  | 'nama_lengkap'
  | 'jenis_kelamin'
  | 'tanggal_lahir'
  | 'nik'
  | 'pendidikan_kode'
  | 'pekerjaan_kode'
  | 'status_perkawinan'
  | 'baptis'

export type KolomKelengkapanKeluarga =
  | 'nomor_kk_katolik'
  | 'lingkungan_id'
  | 'kepala_keluarga_id'
  | 'alamat'

export type PitaUsia = 'anak' | 'sekolah' | 'dewasa'

export const BATAS_PITA = { sekolah: 7, dewasa: 17 } as const

/** The umat columns the rule reads; a SQL row or a form value both fit. */
export interface UmatKelengkapan {
  readonly id: string
  readonly nama_lengkap: string | null
  readonly jenis_kelamin: string | null
  readonly tanggal_lahir: string | null
  readonly nik: string | null
  readonly status_gereja: string | null
  readonly pendidikan_kode: string | null
  readonly pekerjaan_kode: string | null
  readonly status_perkawinan: string | null
  readonly baptis_id: string | null
  /** JSON text as stored, or the parsed object. */
  readonly referensi_sakramen_luar: string | { baptis?: unknown } | null
}

export interface KeluargaKelengkapan {
  readonly nomor_kk_katolik: string | null
  readonly lingkungan_id: string | null
  readonly kepala_keluarga_id: string | null
  readonly alamat: string | null
}

export interface KelengkapanUmat {
  readonly lengkap: boolean
  readonly pita: PitaUsia
  /** Age known from tanggal_lahir; null when it is missing. */
  readonly usia: number | null
  readonly kurang: readonly KolomKelengkapanUmat[]
}

export interface KurangPerKolom {
  readonly kolom: KolomKelengkapanUmat
  readonly jumlah: number
  readonly umatIds: readonly string[]
}

export interface KelengkapanKeluarga {
  readonly lengkap: boolean
  readonly kurangKeluarga: readonly KolomKelengkapanKeluarga[]
  /** Missing umat columns aggregated across members, most frequent first. */
  readonly kurangPerKolom: readonly KurangPerKolom[]
  readonly jumlahUmatBelumLengkap: number
  readonly perUmat: ReadonlyMap<string, KelengkapanUmat>
}

function kosong(value: string | null | undefined): boolean {
  return value === null || value === undefined || value.trim() === ''
}

/** The band of a completed age; an unknown age is the adult band (completeness and the wizards). */
export function pitaDariUsia(usia: number | null): PitaUsia {
  if (usia === null || usia >= BATAS_PITA.dewasa) return 'dewasa'
  if (usia >= BATAS_PITA.sekolah) return 'sekolah'
  return 'anak'
}

function adaReferensiBaptis(umat: UmatKelengkapan): boolean {
  if (!kosong(umat.baptis_id)) return true
  const luar = umat.referensi_sakramen_luar
  if (luar === null || luar === undefined) return false
  if (typeof luar === 'string') {
    try {
      const parsed: unknown = JSON.parse(luar)
      return (
        typeof parsed === 'object' &&
        parsed !== null &&
        (parsed as { baptis?: unknown }).baptis != null
      )
    } catch {
      return false
    }
  }
  return luar.baptis != null
}

/** The columns required for this umat, by age band and church status. */
export function kolomWajibUmat(
  umat: UmatKelengkapan,
  pada: string | Date = new Date(),
): KolomKelengkapanUmat[] {
  const usia = kosong(umat.tanggal_lahir)
    ? null
    : (usiaPada(umat.tanggal_lahir ?? '', pada)?.tahun ?? null)
  const pita = pitaDariUsia(usia)
  const kolom: KolomKelengkapanUmat[] = ['nama_lengkap', 'jenis_kelamin', 'tanggal_lahir']
  if (umat.status_gereja === 'katolik') kolom.push('baptis')
  if (pita !== 'anak') kolom.push('pendidikan_kode')
  if (pita === 'dewasa') kolom.push('nik', 'pekerjaan_kode', 'status_perkawinan')
  return kolom
}

export function kelengkapanUmat(
  umat: UmatKelengkapan,
  pada: string | Date = new Date(),
): KelengkapanUmat {
  const usia = kosong(umat.tanggal_lahir)
    ? null
    : (usiaPada(umat.tanggal_lahir ?? '', pada)?.tahun ?? null)
  const kurang = kolomWajibUmat(umat, pada).filter((kolom) => {
    if (kolom === 'baptis') return !adaReferensiBaptis(umat)
    return kosong(umat[kolom])
  })
  return { lengkap: kurang.length === 0, pita: pitaDariUsia(usia), usia, kurang }
}

export function kolomWajibKeluarga(): KolomKelengkapanKeluarga[] {
  return ['nomor_kk_katolik', 'lingkungan_id', 'kepala_keluarga_id', 'alamat']
}

export function kelengkapanKeluarga(
  keluarga: KeluargaKelengkapan,
  anggota: readonly UmatKelengkapan[],
  pada: string | Date = new Date(),
): KelengkapanKeluarga {
  const kurangKeluarga = kolomWajibKeluarga().filter((kolom) => kosong(keluarga[kolom]))
  const perUmat = new Map<string, KelengkapanUmat>()
  const perKolom = new Map<KolomKelengkapanUmat, string[]>()
  let jumlahUmatBelumLengkap = 0
  for (const umat of anggota) {
    const hasil = kelengkapanUmat(umat, pada)
    perUmat.set(umat.id, hasil)
    if (!hasil.lengkap) jumlahUmatBelumLengkap += 1
    for (const kolom of hasil.kurang) {
      const ids = perKolom.get(kolom) ?? []
      ids.push(umat.id)
      perKolom.set(kolom, ids)
    }
  }
  const kurangPerKolom = [...perKolom.entries()]
    .map(([kolom, umatIds]) => ({ kolom, jumlah: umatIds.length, umatIds }))
    .sort((a, b) => b.jumlah - a.jumlah || a.kolom.localeCompare(b.kolom))
  return {
    lengkap: kurangKeluarga.length === 0 && jumlahUmatBelumLengkap === 0,
    kurangKeluarga,
    kurangPerKolom,
    jumlahUmatBelumLengkap,
    perUmat,
  }
}

// ---------------------------------------------------------------------------
// The completeness line (CONTENT.md section 4): "3 kolom wajib belum diisi:
// Tanggal lahir (2), Pendidikan (1)." on the family card, the same words the
// Beranda table and the list chip rest on.
// ---------------------------------------------------------------------------

export type KolomKelengkapan = KolomKelengkapanUmat | KolomKelengkapanKeluarga

const LABEL_KOLOM: Readonly<Record<KolomKelengkapan, string>> = ui.kelengkapan.kolom

/**
 * The screen label of a completeness column ("Tanggal lahir", "Nomor KK dari paroki"); the family's
 * lingkungan in the parish's word (Sebutan unit terkecil).
 */
export function labelKolomKelengkapan(
  kolom: KolomKelengkapan,
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
): string {
  return isiUnit(LABEL_KOLOM[kolom], sebutan)
}

export interface RingkasanKelengkapan {
  /** Every missing cell counted once: family columns plus one per member per column. */
  readonly jumlah: number
  /** "Tanggal lahir (2), Pendidikan (1)": family columns first, then member columns by frequency. */
  readonly rincian: string
}

/**
 * The parts of the card line for an incomplete family, or null when it is complete. The
 * caller fills `keluarga.kartu.barisKelengkapan` with `jumlah` and `rincian`.
 */
export function ringkasanKelengkapan(
  hasil: KelengkapanKeluarga,
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
): RingkasanKelengkapan | null {
  if (hasil.lengkap) return null
  const bagian: string[] = hasil.kurangKeluarga.map((kolom) =>
    labelKolomKelengkapan(kolom, sebutan),
  )
  let jumlah = hasil.kurangKeluarga.length
  for (const { kolom, jumlah: n } of hasil.kurangPerKolom) {
    bagian.push(`${labelKolomKelengkapan(kolom)} (${n})`)
    jumlah += n
  }
  return { jumlah, rincian: bagian.join(', ') }
}

/** "Tanggal lahir, NIK": the missing columns of one person, for the person record. */
export function rincianKurangUmat(hasil: KelengkapanUmat): string {
  return hasil.kurang.map((kolom) => labelKolomKelengkapan(kolom)).join(', ')
}
